import { Component, Inject, Input, OnInit} from '@angular/core';
import {SERIES_FEEDS, SERIES_INFO, SERIES_LEAGUE_EVENTS, SERIES_OVERVIEW, SERIES_STANDINGS} from 'src/app/app-routing.module';
import {buildLeagueSlug} from '../../../utility/slug-helper';
import {AbstractComponent, CurrentItemType} from "@components";
import {EventItem, EventItemType, League, LeagueEvents, Lookups} from "@model";
import {PublicLeagueDataStore} from "@store";
import {Meta, Title} from "@angular/platform-browser";
import {DOCUMENT} from "@angular/common";
import {environment} from "@environment";
import {TranslocoService} from "@ngneat/transloco";
import {getLanguageValue} from '@utility';
import {ActivatedRoute} from "@angular/router";

export enum LeagueTabs {
  OVERVIEW = 1,
  INFO = 2,
  LEAGUE_EVENTS = 3,
  STANDINGS = 4,
  FEEDS = 5
}

@Component({
    selector: 'app-league-details',
    templateUrl: './league-details.component.html',
    styleUrls: ['./league-details.component.scss'],
    standalone: false
})
export class LeagueDetailsComponent extends AbstractComponent implements OnInit {
  @Input({required: true}) languages: Lookups[] = [];

  tabOverview = SERIES_OVERVIEW;
  tabInfo = SERIES_INFO;
  tabLeagueEvents = SERIES_LEAGUE_EVENTS;
  tabStandings = SERIES_STANDINGS;
  tabFeeds = SERIES_FEEDS;
  useSlugRoutes = false;

  league?: League;
  leagueEvents?: LeagueEvents;

  constructor(
    private publicLeagueDataStore: PublicLeagueDataStore,
    private metaService: Meta,
    private titleService: Title,
    private translocoService: TranslocoService,
    private activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document,
  ) {
    super();
  }

  ngOnInit(): void {
    const slug = this.activatedRoute.snapshot.params['slug'];
    if (slug) {
      this.tabOverview = `/leagues/details/${slug}/overview`;
      this.tabInfo = `/leagues/details/${slug}/info`;
      this.tabLeagueEvents = `/leagues/details/${slug}/events`;
      this.tabStandings = `/leagues/details/${slug}/standings`;
      this.tabFeeds = `/leagues/details/${slug}/feeds`;
      this.useSlugRoutes = true;
    }

    this.subscriptions.push(
        this.publicLeagueDataStore.currentLeague$.subscribe(league => {
          this.league = league;
          if (league) {
            this.setSEOData();
          }
        }),
        this.publicLeagueDataStore.leagueEvents$.subscribe(leagueEvents => {
          this.leagueEvents = leagueEvents;
        }),
    );
  }

  setSEOData(): void {
    const lang = this.translocoService.getActiveLang();
    const title = getLanguageValue(
      this.league?.name, this.league?.nameEng, this.league?.nameIta,
      this.league?.nameSlo, this.league?.nameSr, this.league?.nameBs, lang
    ) ?? environment.site_name;

    const origin = this.document.location.origin;
    const canonicalUrl = this.league?.id
      ? `${origin}/leagues/details/${buildLeagueSlug(this.league.id, this.league)}/overview`
      : `${origin}${this.document.location.pathname}?leagueId=${new URLSearchParams(this.document.location.search).get('leagueId')}`;
    const image = this.league?.imageMedium ?? `${origin}/assets/img/hero2-mobile.webp`;

    this.titleService.setTitle(title);
    this.metaService.updateTag({name: 'description', content: title});

    this.metaService.updateTag({property: 'og:url', content: canonicalUrl});
    this.metaService.updateTag({property: 'og:title', content: title});
    this.metaService.updateTag({property: 'og:type', content: 'website'});
    this.metaService.updateTag({property: 'og:site_name', content: environment.site_name});
    this.metaService.updateTag({property: 'og:description', content: title});
    this.metaService.updateTag({property: 'og:image', content: image});

    this.metaService.updateTag({name: 'twitter:card', content: 'summary_large_image'});
    this.metaService.updateTag({name: 'twitter:title', content: title});
    this.metaService.updateTag({name: 'twitter:description', content: title});
    this.metaService.updateTag({name: 'twitter:image', content: image});

    this.setCanonicalUrl(canonicalUrl);
  }

  private setCanonicalUrl(url: string): void {
    let link = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = this.document.createElement('link') as HTMLLinkElement;
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }


  addToFavoritesAction(eventItem?: EventItem): void {

  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.publicLeagueDataStore.emptyStore();
  }

  protected readonly CurrentItemType = CurrentItemType;
  protected readonly EventItemType = EventItemType;
}
